import { Link } from '@tanstack/react-router'
import { AlertCircle, ImageUp, Info, Layers, MoreHorizontal, Music2, Pencil, Play, Upload } from 'lucide-react'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  useRunChannelGeneration,
  useRunChannelUpload,
  useRunStudioThumbnails,
  useStudioThumbnails,
} from '@/api/endpoints/scheduler'
import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { IconButton } from '@/components/ui/IconButton'
import { formatWhen, type ChannelCardModel, type ChannelHealth, type ChannelPlatform } from '@/features/scheduler/channelCards'
import { coversRunMessage } from '@/features/scheduler/components/StudioThumbnailsBanner/status'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import styles from './ChannelCard.module.scss'

const HEALTH_LABEL: Record<ChannelHealth, string> = {
  ok: 'Geração e upload em dia',
  running: 'Rodando agora',
  failed: 'A última execução falhou',
  partial: 'Parte da última execução falhou',
  idle: 'Ainda não rodou',
  off: 'Desativado',
}

// Os ícones de marca do lucide estão depreciados: os dois glifos são traços
// simples no mesmo estilo (24 px, traço 2).
const PLATFORM: Record<ChannelPlatform, { label: string; icon: ReactNode }> = {
  youtube: {
    label: 'YouTube',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="4" />
        <path d="M10 9l5 3-5 3z" fill="currentColor" />
      </svg>
    ),
  },
  instagram: {
    label: 'Instagram',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  tiktok: { label: 'TikTok', icon: <Music2 /> },
}

interface ChannelCardProps {
  card: ChannelCardModel
  /** O canal escolhido na barra lateral. */
  selected: boolean
  now: Date
  onEdit: () => void
  onDrafts: () => void
  onDetails: () => void
}

/**
 * Um canal na grade do Scheduler. O card inteiro abre a edição; as ações
 * rápidas (gerar, publicar, detalhes) aparecem no hover e, no toque, viram o
 * menu "⋯". Só o que pede atenção ganha texto: a falha e os selos de
 * pendência. O resto é a foto, o nome, a factory e a próxima geração.
 */
export function ChannelCard({ card, selected, now, onEdit, onDrafts, onDetails }: ChannelCardProps) {
  const runGen = useRunChannelGeneration()
  const runUpl = useRunChannelUpload()
  const runCovers = useRunStudioThumbnails()
  // A mesma consulta da faixa de capas (o React Query não repete a chamada).
  const { data: covers } = useStudioThumbnails()
  // Só o admin drena (a conta Google do Studio e o celular são dele).
  const canApplyCovers = card.pendingCovers > 0 && Boolean(covers?.is_admin)
  const coversBusy = runCovers.isPending || Boolean(covers?.running)
  // Canal ligado a pool não gera: o play gera o pool, depois de confirmar,
  // porque a geração vale para todos os canais ligados a ele.
  const [confirmPool, setConfirmPool] = useState(false)

  const generatePool = () => {
    if (!card.poolSlug) return
    const poolName = card.poolName ?? card.poolSlug
    runGen.mutate(card.poolSlug, {
      onSuccess: () => {
        setConfirmPool(false)
        toast.success(`Geração do pool disparada — ${poolName}`)
      },
      onError: (err) => {
        toast.error(errorMessage(err, `Não foi possível gerar o pool ${poolName}`))
      },
    })
  }

  const generate = () => {
    if (card.poolSlug) {
      setConfirmPool(true)
      return
    }
    runGen.mutate(card.slug, {
      onSuccess: () => {
        toast.success(`Geração disparada — ${card.name}`)
      },
      onError: (err) => {
        toast.error(errorMessage(err, `Não foi possível gerar ${card.name}`))
      },
    })
  }
  const applyCovers = () => {
    runCovers.mutate(card.slug, {
      onSuccess: (data) => {
        const message = coversRunMessage(data, card.name)
        if (message) {
          toast.success(message)
        } else {
          toast.info(`Nenhuma capa pendente em ${card.name}`)
        }
      },
      onError: (err) => {
        toast.error(errorMessage(err, `Não foi possível aplicar as capas de ${card.name}`))
      },
    })
  }
  const publish = () => {
    runUpl.mutate(card.slug, {
      onSuccess: () => {
        toast.success(`Upload disparado — ${card.name}`)
      },
      onError: (err) => {
        toast.error(errorMessage(err, `Não foi possível publicar ${card.name}`))
      },
    })
  }

  // Canal ligado a pool não gera: o card fala da próxima publicação.
  const fromPool = card.poolSlug !== null
  const when = fromPool
    ? card.enabled
      ? formatWhen(card.nextUploadAt, now)
      : '—'
    : card.generating
      ? 'Gerando agora'
      : card.enabled
        ? formatWhen(card.nextRunAt, now)
        : '—'
  const perDay = `${String(card.videosPerDay)} ${card.videosPerDay === 1 ? 'vídeo' : 'vídeos'}/dia`
  const meta = !card.enabled
    ? 'Sem geração agendada'
    : card.generating
      ? `${perDay} · depois ${formatWhen(card.nextRunAt, now)}`
      : [perDay, card.slots.join(' · ')].filter(Boolean).join(' · ')
  const hasBadges = card.drafts > 0 || card.pendingCovers > 0 || !card.enabled

  return (
    <article
      className={cn(styles.card, selected && styles.selected, !card.enabled && styles.off)}
      style={{ '--accent': card.accent } as CSSProperties}
    >
      <button type="button" className={styles.hit} aria-label={`Editar ${card.name}`} onClick={onEdit} />

      <div className={styles.quick}>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={fromPool ? `Gerar o pool agora — ${card.name}` : `Gerar agora — ${card.name}`}
          title={fromPool ? 'Gerar o pool agora' : 'Gerar agora'}
          disabled={!card.enabled || runGen.isPending}
          onClick={generate}
        >
          <Play />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={`Publicar agora — ${card.name}`}
          title="Publicar agora"
          disabled={!card.enabled || runUpl.isPending}
          onClick={publish}
        >
          <Upload />
        </IconButton>
        {canApplyCovers ? (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={`Aplicar capas pendentes — ${card.name}`}
            title={coversBusy ? 'Já há uma drenagem de capas rodando' : 'Aplicar as capas pendentes deste canal'}
            disabled={coversBusy}
            onClick={applyCovers}
          >
            <ImageUp />
          </IconButton>
        ) : null}
        <IconButton variant="ghost" size="sm" aria-label={`Detalhes — ${card.name}`} title="Detalhes" onClick={onDetails}>
          <Info />
        </IconButton>
      </div>

      <div className={styles.touchMenu}>
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <IconButton variant="ghost" size="md" aria-label={`Ações — ${card.name}`}>
              <MoreHorizontal />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem leadingIcon={<Pencil />} onSelect={onEdit}>
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem leadingIcon={<Play />} disabled={!card.enabled || runGen.isPending} onSelect={generate}>
              {fromPool ? 'Gerar o pool agora' : 'Gerar agora'}
            </DropdownMenuItem>
            <DropdownMenuItem leadingIcon={<Upload />} disabled={!card.enabled || runUpl.isPending} onSelect={publish}>
              Publicar agora
            </DropdownMenuItem>
            {canApplyCovers ? (
              <DropdownMenuItem leadingIcon={<ImageUp />} disabled={coversBusy} onSelect={applyCovers}>
                Aplicar capas pendentes
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem leadingIcon={<Info />} onSelect={onDetails}>
              Detalhes
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenuRoot>
      </div>

      <div className={styles.head}>
        <ChannelAvatar key={card.avatarUrl ?? ''} url={card.avatarUrl} initials={card.initials} />
        <div className={styles.identity}>
          <h3 className={styles.name}>{card.name}</h3>
          <div className={styles.tags}>
            {card.factoryLabel ? <span className={styles.factory}>{card.factoryLabel}</span> : null}
            {card.platforms.map((p) => (
              <span key={p} className={styles.platform} title={PLATFORM[p].label} role="img" aria-label={PLATFORM[p].label}>
                {PLATFORM[p].icon}
              </span>
            ))}
            {card.llmProvider === 'local' ? (
              <span className={styles.local} title="Roteiro e metadados no Ollama local">
                Ollama
              </span>
            ) : null}
          </div>
        </div>
        <span
          className={cn(styles.dot, styles[`dot-${card.health}`])}
          role="img"
          aria-label={HEALTH_LABEL[card.health]}
          title={HEALTH_LABEL[card.health]}
        />
      </div>

      {fromPool ? (
        <div className={styles.next}>
          <span className={styles.caption}>Próxima publicação</span>
          <span className={styles.when}>{when}</span>
          <Link
            className={styles.poolLink}
            to="/pools/$slug"
            params={{ slug: card.poolSlug ?? '' }}
          >
            <Layers aria-hidden />
            Vídeos do pool <strong>{card.poolName}</strong>
          </Link>
        </div>
      ) : (
        <div className={styles.next}>
          <span className={styles.caption}>Próxima geração</span>
          <span className={cn(styles.when, card.generating && styles.whenRunning)}>{when}</span>
          <span className={styles.meta}>{meta}</span>
        </div>
      )}

      {card.failure ? (
        <p className={cn(styles.failure, card.failure.partial && styles.failurePartial)}>
          <AlertCircle aria-hidden />
          {card.failure.text}
        </p>
      ) : null}

      {hasBadges ? (
        <div className={styles.badges}>
          {card.pendingCovers > 0 ? (
            <span className={cn(styles.badge, styles.badgeCovers)}>
              <ImageUp aria-hidden />
              {card.pendingCovers} {card.pendingCovers === 1 ? 'capa pendente' : 'capas pendentes'}
            </span>
          ) : null}
          {card.drafts > 0 ? (
            <button type="button" className={cn(styles.badge, styles.badgeDrafts)} onClick={onDrafts}>
              <Pencil aria-hidden />
              {card.drafts} {card.drafts === 1 ? 'rascunho para aprovar' : 'rascunhos para aprovar'}
            </button>
          ) : null}
          {!card.enabled ? <span className={cn(styles.badge, styles.badgeOff)}>Desativado</span> : null}
        </div>
      ) : null}
      {fromPool ? (
        <DialogRoot
          open={confirmPool}
          onOpenChange={(next) => {
            if (!runGen.isPending) setConfirmPool(next)
          }}
        >
          <DialogContent
            title={`Gerar o pool ${card.poolName ?? card.poolSlug ?? ''} agora?`}
            description={`${card.name} não gera sozinho: publica o que o pool gera.`}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="secondary" disabled={runGen.isPending}>
                    Cancelar
                  </Button>
                </DialogClose>
                <Button onClick={generatePool} disabled={runGen.isPending}>
                  {runGen.isPending ? 'Disparando…' : 'Gerar o pool'}
                </Button>
              </>
            }
          >
            <p className={styles.confirmText}>
              A geração vale para todos os canais ligados ao pool, e os vídeos entram no estoque dele. Cada canal
              pega os seus na hora do upload.
            </p>
          </DialogContent>
        </DialogRoot>
      ) : null}
    </article>
  )
}

/** A foto do canal; sem foto (ou se ela não carregar), as iniciais na cor da factory. */
function ChannelAvatar({ url, initials }: { url: string | null; initials: string }) {
  const [broken, setBroken] = useState(false)
  if (url && !broken) {
    return (
      <img
        className={styles.avatar}
        src={url}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          setBroken(true)
        }}
      />
    )
  }
  return (
    <span className={cn(styles.avatar, styles.initials)} aria-hidden>
      {initials}
    </span>
  )
}
